import { RESOURCE_LIMITS, estimateNominalOutputPoints } from '../config.js';
/**
 * AeroControl Studio reference kernel.
 *
 * This is an initial framework-independent scaffold, not the complete product.
 * Formula authority: ../CALCULATION_REFERENCE.md (aerocontrol-engine/1.0).
 */

export type ModelMode = 'spacecraft_single_axis' | 'aircraft_pitch_surrogate';
export type DiscontinuitySide = 'left' | 'right';

export interface PlantParameters {
  readonly inertia: number;
  readonly damping: number;
  readonly stiffness: number;
}

export interface ControllerParameters {
  readonly kp: number;
  readonly ki: number;
  readonly kd: number;
  readonly derivativeFilterTimeConstant: number;
  readonly antiWindup:
    | { readonly enabled: false }
    | { readonly enabled: true; readonly trackingTimeConstant: number };
}

export interface ActuatorParameters {
  readonly maximumTorque: number;
}

export type ReferenceProfile =
  | { readonly kind: 'constant'; readonly value: number }
  | {
      readonly kind: 'step';
      readonly time: number;
      readonly before: number;
      readonly after: number;
    }
  | {
      readonly kind: 'piecewise_constant';
      readonly initial: number;
      readonly events: readonly { readonly time: number; readonly value: number }[];
    };

export type DisturbanceProfile =
  | { readonly kind: 'none' }
  | { readonly kind: 'constant'; readonly value: number }
  | { readonly kind: 'step'; readonly time: number; readonly amplitude: number }
  | {
      readonly kind: 'rectangular_pulse';
      readonly startTime: number;
      readonly endTime: number;
      readonly amplitude: number;
    }
  | {
      readonly kind: 'impulse_equivalent_pulse';
      readonly startTime: number;
      readonly duration: number;
      readonly angularImpulse: number;
    }
  | {
      readonly kind: 'sinusoidal';
      readonly amplitude: number;
      readonly frequencyHz: number;
      readonly phaseRad: number;
    };

export interface NoiseParameters {
  readonly enabled: boolean;
  readonly seed: number;
  readonly angleStandardDeviation: number;
  readonly rateStandardDeviation: number;
}

export interface InitialState {
  readonly angle: number;
  readonly angularRate: number;
  readonly integralTorque: number;
  readonly filteredMeasuredRate: number;
}

export interface SimulationSettings {
  readonly startTime: number;
  readonly endTime: number;
  readonly nominalStep: number;
  readonly abortAbsoluteStateBound?: number;
}

export interface ControlScenario {
  readonly mode: ModelMode;
  readonly plant: PlantParameters;
  readonly controller: ControllerParameters;
  readonly actuator: ActuatorParameters;
  readonly reference: ReferenceProfile;
  readonly disturbance: DisturbanceProfile;
  readonly noise: NoiseParameters;
  readonly initialState: InitialState;
  readonly simulation: SimulationSettings;
}

export type StateVector = readonly [
  angle: number,
  angularRate: number,
  integralTorque: number,
  filteredMeasuredRate: number,
];

export interface NoisePair {
  readonly angle: number;
  readonly rate: number;
}

export interface ControllerEvaluation {
  readonly error: number;
  readonly measuredAngle: number;
  readonly measuredRate: number;
  readonly rateUsedByDerivative: number;
  readonly rawTorque: number;
  readonly appliedTorque: number;
  readonly saturationExceeded: boolean;
  readonly integralDerivative: number;
}

export interface ClosedLoopEvaluation extends ControllerEvaluation {
  readonly referenceAngle: number;
  readonly disturbanceTorque: number;
  readonly derivative: StateVector;
}

export interface SimulationSample {
  readonly time: number;
  readonly referenceAngle: number;
  readonly angle: number;
  readonly angularRate: number;
  readonly measuredAngle: number;
  readonly measuredRate: number;
  readonly error: number;
  readonly integralTorque: number;
  /** Dynamic filter state; inactive and canonically zero when tau_f = 0. */
  readonly filteredMeasuredRate: number;
  /** Actual measured-rate signal used by the derivative term for this sample. */
  readonly rateUsedByDerivative: number;
  readonly rawTorque: number;
  readonly appliedTorque: number;
  readonly disturbanceTorque: number;
  readonly saturationExceeded: boolean;
}

export interface SimulationResult {
  readonly state: 'completed';
  readonly samples: readonly SimulationSample[];
  readonly eventAlignedGrid: readonly number[];
}

export type StabilityBranch =
  | 'second_order_no_integral_no_filter'
  | 'cubic_integral_no_filter'
  | 'cubic_no_integral_with_filter'
  | 'quartic_integral_with_filter';

export interface StabilityCondition {
  readonly id: string;
  readonly description: string;
  readonly left: number;
  readonly comparator: '>';
  readonly right: number;
  readonly passed: boolean;
}

export interface StabilityResult {
  readonly state:
    | 'stable_for_scoped_linear_model'
    | 'not_strictly_stable_for_scoped_linear_model';
  readonly branch: StabilityBranch;
  readonly coefficientsDescending: readonly number[];
  readonly conditions: readonly StabilityCondition[];
}

export interface StepMetricSettings {
  readonly riseLowerFraction: number;
  readonly riseUpperFraction: number;
  readonly settlingBand:
    | { readonly kind: 'absolute'; readonly value: number }
    | { readonly kind: 'fraction_of_step'; readonly value: number };
  readonly settlingHoldDuration: number;
}

export interface StepMetrics {
  readonly riseTime: number | null;
  readonly riseTimeReason: string | null;
  readonly overshootPercent: number | null;
  readonly overshootReason: string | null;
  readonly settlingTime: number | null;
  readonly settlingReason: string | null;
  readonly finalError: number;
}

export interface IntegralMetrics {
  readonly iae: number;
  readonly ise: number;
  readonly itae: number;
  readonly controlAbsolute: number;
  readonly controlSquared: number;
}

export interface SaturationMetrics {
  readonly duration: number;
  readonly fraction: number;
  readonly maximumRawMagnitude: number;
  readonly maximumAppliedMagnitude: number;
  readonly firstExceedanceTime: number | null;
}

export interface RefinementCriterion {
  readonly selected: boolean;
  readonly tolerance: number;
}

export interface RefinementCriteria {
  readonly angle: RefinementCriterion;
  readonly angularRate: RefinementCriterion;
  readonly appliedTorque: RefinementCriterion;
}

export interface MaximumDifference {
  readonly absolute: number;
  readonly signed: number;
  readonly time: number;
  readonly state: 'met' | 'not_met' | 'not_assessed';
  readonly tolerance: number | null;
}

export interface RefinementResult {
  readonly state: 'met' | 'not_met' | 'not_assessed';
  readonly reason: string;
  readonly angle: MaximumDifference;
  readonly angularRate: MaximumDifference;
  readonly appliedTorque: MaximumDifference;
}

export interface TuningCandidate {
  readonly id: string;
  readonly feasible: boolean;
  readonly objective: number | null;
  readonly overshootPercent: number | null;
  readonly saturationFraction: number | null;
  readonly controlSquared: number | null;
  readonly kp: number;
  readonly ki: number;
  readonly kd: number;
  readonly derivativeFilterTimeConstant: number;
  readonly trackingTimeConstant: number;
}

function requireFinite(name: string, value: number): void {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite.`);
  }
}

function requireNonNegative(name: string, value: number): void {
  requireFinite(name, value);
  if (value < 0) {
    throw new RangeError(`${name} must be non-negative.`);
  }
}

function requirePositive(name: string, value: number): void {
  requireFinite(name, value);
  if (value <= 0) {
    throw new RangeError(`${name} must be positive.`);
  }
}

export function degToRad(value: number): number {
  requireFinite('angle degrees', value);
  return (value * Math.PI) / 180;
}

export function radToDeg(value: number): number {
  requireFinite('angle radians', value);
  return (value * 180) / Math.PI;
}

export function wrapToPi(value: number): number {
  requireFinite('angle', value);
  const twoPi = 2 * Math.PI;
  return ((((value + Math.PI) % twoPi) + twoPi) % twoPi) - Math.PI;
}

export function calculateError(
  mode: ModelMode,
  referenceAngle: number,
  measuredAngle: number,
): number {
  requireFinite('referenceAngle', referenceAngle);
  requireFinite('measuredAngle', measuredAngle);
  const raw = referenceAngle - measuredAngle;
  return mode === 'spacecraft_single_axis' ? wrapToPi(raw) : raw;
}

/** Machine-scale comparison used only to reconcile constructed time representations. */
export function sameConstructedTime(a: number, b: number): boolean {
  requireFinite('time a', a);
  requireFinite('time b', b);
  return (
    Math.abs(a - b) <=
    8 * Number.EPSILON * Math.max(1, Math.abs(a), Math.abs(b))
  );
}

export function evaluateReference(
  profile: ReferenceProfile,
  time: number,
  side: DiscontinuitySide = 'right',
): number {
  requireFinite('time', time);
  switch (profile.kind) {
    case 'constant':
      return profile.value;
    case 'step':
      if (sameConstructedTime(time, profile.time)) {
        return side === 'left' ? profile.before : profile.after;
      }
      return time < profile.time ? profile.before : profile.after;
    case 'piecewise_constant': {
      let value = profile.initial;
      for (const event of profile.events) {
        const atEvent = sameConstructedTime(time, event.time);
        if (time > event.time && !atEvent) {
          value = event.value;
          continue;
        }
        if (atEvent && side === 'right') {
          value = event.value;
          continue;
        }
        if (time < event.time || (atEvent && side === 'left')) {
          break;
        }
      }
      return value;
    }
  }
}

export function evaluateDisturbance(
  profile: DisturbanceProfile,
  time: number,
  side: DiscontinuitySide = 'right',
): number {
  requireFinite('time', time);
  switch (profile.kind) {
    case 'none':
      return 0;
    case 'constant':
      return profile.value;
    case 'step': {
      if (sameConstructedTime(time, profile.time)) {
        return side === 'left' ? 0 : profile.amplitude;
      }
      return time < profile.time ? 0 : profile.amplitude;
    }
    case 'rectangular_pulse': {
      const atStart = sameConstructedTime(time, profile.startTime);
      const atEnd = sameConstructedTime(time, profile.endTime);
      if (atStart) {
        return side === 'left' ? 0 : profile.amplitude;
      }
      if (atEnd) {
        return side === 'left' ? profile.amplitude : 0;
      }
      return time > profile.startTime && time < profile.endTime
        ? profile.amplitude
        : 0;
    }
    case 'impulse_equivalent_pulse': {
      requirePositive('impulse pulse duration', profile.duration);
      return evaluateDisturbance(
        {
          kind: 'rectangular_pulse',
          startTime: profile.startTime,
          endTime: profile.startTime + profile.duration,
          amplitude: profile.angularImpulse / profile.duration,
        },
        time,
        side,
      );
    }
    case 'sinusoidal':
      return (
        profile.amplitude *
        Math.sin(2 * Math.PI * profile.frequencyHz * time + profile.phaseRad)
      );
  }
}

function collectEventTimes(
  reference: ReferenceProfile,
  disturbance: DisturbanceProfile,
): number[] {
  const result: number[] = [];
  if (reference.kind === 'step') {
    result.push(reference.time);
  } else if (reference.kind === 'piecewise_constant') {
    result.push(...reference.events.map((event) => event.time));
  }
  if (disturbance.kind === 'step') {
    result.push(disturbance.time);
  } else if (disturbance.kind === 'rectangular_pulse') {
    result.push(disturbance.startTime, disturbance.endTime);
  } else if (disturbance.kind === 'impulse_equivalent_pulse') {
    result.push(disturbance.startTime, disturbance.startTime + disturbance.duration);
  }
  return result;
}

export function buildEventAlignedGrid(
  settings: SimulationSettings,
  reference: ReferenceProfile,
  disturbance: DisturbanceProfile,
): number[] {
  const { startTime, endTime, nominalStep } = settings;
  requireFinite('startTime', startTime);
  requireFinite('endTime', endTime);
  requirePositive('nominalStep', nominalStep);
  if (endTime <= startTime) {
    throw new RangeError('endTime must be greater than startTime.');
  }
  const estimatedNominalPoints = estimateNominalOutputPoints(startTime, endTime, nominalStep);
  if (estimatedNominalPoints > RESOURCE_LIMITS.simulationOutputPoints) {
    throw new RangeError(
      `Simulation would create approximately ${estimatedNominalPoints} nominal output points, exceeding the disclosed software limit ${RESOURCE_LIMITS.simulationOutputPoints}. Increase nominalStep or shorten the interval.`,
    );
  }
  const entries: { time: number; explicitEvent: boolean }[] = [
    { time: startTime, explicitEvent: true },
    { time: endTime, explicitEvent: true },
  ];
  const nominalCount = Math.floor((endTime - startTime) / nominalStep);
  for (let index = 1; index <= nominalCount; index += 1) {
    const time = startTime + index * nominalStep;
    if (time > startTime && time < endTime && !sameConstructedTime(time, endTime)) {
      entries.push({ time, explicitEvent: false });
    }
  }
  for (const time of collectEventTimes(reference, disturbance)) {
    requireFinite('event time', time);
    if (time < startTime || time > endTime) {
      throw new RangeError(`Event time ${time} lies outside the simulation interval.`);
    }
    entries.push({ time, explicitEvent: true });
  }
  entries.sort((a, b) => a.time - b.time || Number(b.explicitEvent) - Number(a.explicitEvent));
  const merged: { time: number; explicitEvent: boolean }[] = [];
  for (const entry of entries) {
    const prior = merged.at(-1);
    if (prior !== undefined && sameConstructedTime(prior.time, entry.time)) {
      if (entry.explicitEvent && !prior.explicitEvent) {
        merged[merged.length - 1] = entry;
      }
      continue;
    }
    merged.push(entry);
  }
  const grid = merged.map((entry) => entry.time);
  if (grid.length > RESOURCE_LIMITS.simulationOutputPoints) {
    throw new RangeError(
      `Event alignment would create ${grid.length} output points, exceeding the disclosed software limit ${RESOURCE_LIMITS.simulationOutputPoints}.`,
    );
  }
  grid[0] = startTime;
  grid[grid.length - 1] = endTime;
  for (let index = 0; index < grid.length - 1; index += 1) {
    const current = grid[index];
    const next = grid[index + 1];
    if (current === undefined || next === undefined || !(next > current)) {
      throw new RangeError('Event grid contains a non-positive interval.');
    }
  }
  return grid;
}

/** Mulberry32; returned values are in [0,1). */
export function createMulberry32(seed: number): () => number {
  requireFinite('seed', seed);
  if (!Number.isInteger(seed) || seed < 0 || seed > 0xffff_ffff) {
    throw new RangeError('seed must be an unsigned 32-bit integer (0 to 4294967295).');
  }
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

export function createGaussianPairGenerator(seed: number): () => readonly [number, number] {
  const uniform = createMulberry32(seed);
  return () => {
    const u1 = Math.max(uniform(), Number.MIN_VALUE);
    const u2 = uniform();
    const radius = Math.sqrt(-2 * Math.log(u1));
    const angle = 2 * Math.PI * u2;
    return [radius * Math.cos(angle), radius * Math.sin(angle)] as const;
  };
}

export function evaluateController(input: {
  readonly mode: ModelMode;
  readonly referenceAngle: number;
  readonly physicalAngle: number;
  readonly physicalRate: number;
  readonly filteredMeasuredRate: number;
  readonly integralTorque: number;
  readonly noise: NoisePair;
  readonly controller: ControllerParameters;
  readonly actuator: ActuatorParameters;
}): ControllerEvaluation {
  const measuredAngle = input.physicalAngle + input.noise.angle;
  const measuredRate = input.physicalRate + input.noise.rate;
  const error = calculateError(input.mode, input.referenceAngle, measuredAngle);
  const rateUsedByDerivative =
    input.controller.derivativeFilterTimeConstant === 0
      ? measuredRate
      : input.filteredMeasuredRate;
  const rawTorque =
    input.controller.kp * error +
    input.integralTorque -
    input.controller.kd * rateUsedByDerivative;
  const limit = input.actuator.maximumTorque;
  const appliedTorque = Math.min(limit, Math.max(-limit, rawTorque));
  const antiWindupTerm = input.controller.antiWindup.enabled
    ? (appliedTorque - rawTorque) /
      input.controller.antiWindup.trackingTimeConstant
    : 0;
  return {
    error,
    measuredAngle,
    measuredRate,
    rateUsedByDerivative,
    rawTorque,
    appliedTorque,
    saturationExceeded: Math.abs(rawTorque) > limit,
    integralDerivative: input.controller.ki * error + antiWindupTerm,
  };
}

export function evaluateClosedLoop(
  scenario: ControlScenario,
  time: number,
  state: StateVector,
  noise: NoisePair,
  side: DiscontinuitySide,
): ClosedLoopEvaluation {
  const [angle, angularRate, integralTorque, filteredMeasuredRate] = state;
  const referenceAngle = evaluateReference(scenario.reference, time, side);
  const controller = evaluateController({
    mode: scenario.mode,
    referenceAngle,
    physicalAngle: angle,
    physicalRate: angularRate,
    filteredMeasuredRate,
    integralTorque,
    noise,
    controller: scenario.controller,
    actuator: scenario.actuator,
  });
  const disturbanceTorque = evaluateDisturbance(scenario.disturbance, time, side);
  const angularAcceleration =
    (controller.appliedTorque +
      disturbanceTorque -
      scenario.plant.damping * angularRate -
      scenario.plant.stiffness * angle) /
    scenario.plant.inertia;
  const filterDerivative =
    scenario.controller.derivativeFilterTimeConstant === 0
      ? 0
      : (controller.measuredRate - filteredMeasuredRate) /
        scenario.controller.derivativeFilterTimeConstant;
  return {
    ...controller,
    referenceAngle,
    disturbanceTorque,
    derivative: [
      angularRate,
      angularAcceleration,
      controller.integralDerivative,
      filterDerivative,
    ],
  };
}

function addScaled(state: StateVector, delta: StateVector, scale: number): StateVector {
  return [
    state[0] + scale * delta[0],
    state[1] + scale * delta[1],
    state[2] + scale * delta[2],
    state[3] + scale * delta[3],
  ];
}

export function rk4Step(
  state: StateVector,
  time: number,
  step: number,
  derivative: (
    time: number,
    state: StateVector,
    side: DiscontinuitySide,
  ) => StateVector,
): StateVector {
  requirePositive('RK4 step', step);
  const k1 = derivative(time, state, 'right');
  const k2 = derivative(time + step / 2, addScaled(state, k1, step / 2), 'right');
  const k3 = derivative(time + step / 2, addScaled(state, k2, step / 2), 'right');
  const k4 = derivative(time + step, addScaled(state, k3, step), 'left');
  return [
    state[0] + (step * (k1[0] + 2 * k2[0] + 2 * k3[0] + k4[0])) / 6,
    state[1] + (step * (k1[1] + 2 * k2[1] + 2 * k3[1] + k4[1])) / 6,
    state[2] + (step * (k1[2] + 2 * k2[2] + 2 * k3[2] + k4[2])) / 6,
    state[3] + (step * (k1[3] + 2 * k2[3] + 2 * k3[3] + k4[3])) / 6,
  ];
}

export function validateScenario(scenario: ControlScenario): void {
  if (
    scenario.mode !== 'spacecraft_single_axis' &&
    scenario.mode !== 'aircraft_pitch_surrogate'
  ) {
    throw new RangeError('mode must be spacecraft_single_axis or aircraft_pitch_surrogate.');
  }
  requirePositive('plant.inertia', scenario.plant.inertia);
  // Signed damping/stiffness are permitted so destabilising linear surrogates can be studied.
  requireFinite('plant.damping', scenario.plant.damping);
  requireFinite('plant.stiffness', scenario.plant.stiffness);
  requireNonNegative('controller.kp', scenario.controller.kp);
  requireNonNegative('controller.ki', scenario.controller.ki);
  requireNonNegative('controller.kd', scenario.controller.kd);
  requireNonNegative(
    'controller.derivativeFilterTimeConstant',
    scenario.controller.derivativeFilterTimeConstant,
  );
  if (scenario.controller.antiWindup.enabled) {
    requirePositive(
      'controller.antiWindup.trackingTimeConstant',
      scenario.controller.antiWindup.trackingTimeConstant,
    );
  }
  requirePositive('actuator.maximumTorque', scenario.actuator.maximumTorque);
  requireNonNegative(
    'noise.angleStandardDeviation',
    scenario.noise.angleStandardDeviation,
  );
  requireNonNegative(
    'noise.rateStandardDeviation',
    scenario.noise.rateStandardDeviation,
  );
  if (
    !Number.isInteger(scenario.noise.seed) ||
    scenario.noise.seed < 0 ||
    scenario.noise.seed > 0xffff_ffff
  ) {
    throw new RangeError('Noise seed must be an unsigned 32-bit integer (0 to 4294967295).');
  }
  for (const [name, value] of Object.entries(scenario.initialState)) {
    requireFinite(`initialState.${name}`, value);
  }
  if (
    scenario.controller.derivativeFilterTimeConstant === 0 &&
    scenario.initialState.filteredMeasuredRate !== 0
  ) {
    throw new RangeError(
      'initialState.filteredMeasuredRate must be exactly zero when the derivative filter is inactive.',
    );
  }
  if (scenario.simulation.abortAbsoluteStateBound !== undefined) {
    requirePositive(
      'simulation.abortAbsoluteStateBound',
      scenario.simulation.abortAbsoluteStateBound,
    );
  }

  switch (scenario.reference.kind) {
    case 'constant':
      requireFinite('reference.value', scenario.reference.value);
      break;
    case 'step':
      requireFinite('reference.time', scenario.reference.time);
      requireFinite('reference.before', scenario.reference.before);
      requireFinite('reference.after', scenario.reference.after);
      break;
    case 'piecewise_constant': {
      requireFinite('reference.initial', scenario.reference.initial);
      if (scenario.reference.events.length > RESOURCE_LIMITS.referenceProfileEvents) {
        throw new RangeError(
          `Piecewise reference contains ${scenario.reference.events.length} events, exceeding the disclosed software limit ${RESOURCE_LIMITS.referenceProfileEvents}.`,
        );
      }
      let previous = -Infinity;
      for (const event of scenario.reference.events) {
        requireFinite('reference event time', event.time);
        requireFinite('reference event value', event.value);
        if (event.time <= previous) {
          throw new RangeError('Piecewise reference event times must be strictly increasing.');
        }
        previous = event.time;
      }
      break;
    }
  }

  switch (scenario.disturbance.kind) {
    case 'none':
      break;
    case 'constant':
      requireFinite('disturbance.value', scenario.disturbance.value);
      break;
    case 'step':
      requireFinite('disturbance.time', scenario.disturbance.time);
      requireFinite('disturbance.amplitude', scenario.disturbance.amplitude);
      break;
    case 'rectangular_pulse':
      requireFinite('disturbance.startTime', scenario.disturbance.startTime);
      requireFinite('disturbance.endTime', scenario.disturbance.endTime);
      requireFinite('disturbance.amplitude', scenario.disturbance.amplitude);
      if (!(scenario.disturbance.endTime > scenario.disturbance.startTime)) {
        throw new RangeError('Rectangular-pulse endTime must be greater than startTime.');
      }
      break;
    case 'impulse_equivalent_pulse':
      requireFinite('disturbance.startTime', scenario.disturbance.startTime);
      requirePositive('impulse-equivalent duration', scenario.disturbance.duration);
      requireFinite('disturbance.angularImpulse', scenario.disturbance.angularImpulse);
      break;
    case 'sinusoidal':
      requireFinite('disturbance.amplitude', scenario.disturbance.amplitude);
      requireNonNegative('sinusoidal frequency', scenario.disturbance.frequencyHz);
      requireFinite('disturbance.phaseRad', scenario.disturbance.phaseRad);
      break;
  }

  buildEventAlignedGrid(
    scenario.simulation,
    scenario.reference,
    scenario.disturbance,
  );
}

function sampleFromEvaluation(
  time: number,
  state: StateVector,
  evaluation: ClosedLoopEvaluation,
): SimulationSample {
  return {
    time,
    referenceAngle: evaluation.referenceAngle,
    angle: state[0],
    angularRate: state[1],
    measuredAngle: evaluation.measuredAngle,
    measuredRate: evaluation.measuredRate,
    error: evaluation.error,
    integralTorque: state[2],
    filteredMeasuredRate: state[3],
    rateUsedByDerivative: evaluation.rateUsedByDerivative,
    rawTorque: evaluation.rawTorque,
    appliedTorque: evaluation.appliedTorque,
    disturbanceTorque: evaluation.disturbanceTorque,
    saturationExceeded: evaluation.saturationExceeded,
  };
}

export function simulateScenario(scenario: ControlScenario): SimulationResult {
  validateScenario(scenario);
  const grid = buildEventAlignedGrid(
    scenario.simulation,
    scenario.reference,
    scenario.disturbance,
  );
  const gaussian = scenario.noise.enabled
    ? createGaussianPairGenerator(scenario.noise.seed)
    : null;
  let state: StateVector = [
    scenario.initialState.angle,
    scenario.initialState.angularRate,
    scenario.initialState.integralTorque,
    scenario.initialState.filteredMeasuredRate,
  ];
  const samples: SimulationSample[] = [];
  let lastNoise: NoisePair = { angle: 0, rate: 0 };
  for (let index = 0; index < grid.length - 1; index += 1) {
    const time = grid[index];
    const nextTime = grid[index + 1];
    if (time === undefined || nextTime === undefined) {
      throw new Error('Unexpected missing event-grid point.');
    }
    const standardPair = gaussian === null ? ([0, 0] as const) : gaussian();
    const noise: NoisePair = {
      angle: standardPair[0] * scenario.noise.angleStandardDeviation,
      rate: standardPair[1] * scenario.noise.rateStandardDeviation,
    };
    lastNoise = noise;
    const evaluation = evaluateClosedLoop(scenario, time, state, noise, 'right');
    samples.push(sampleFromEvaluation(time, state, evaluation));
    const step = nextTime - time;
    state = rk4Step(state, time, step, (stageTime, stageState, side) =>
      evaluateClosedLoop(scenario, stageTime, stageState, noise, side).derivative,
    );
    if (state.some((value) => !Number.isFinite(value))) {
      throw new Error(`Numerical failure: non-finite state after t=${nextTime}.`);
    }
    if (
      scenario.simulation.abortAbsoluteStateBound !== undefined &&
      state.some((value) => Math.abs(value) > scenario.simulation.abortAbsoluteStateBound!)
    ) {
      throw new Error(
        `Simulation aborted by recorded state bound ${scenario.simulation.abortAbsoluteStateBound}.`,
      );
    }
  }
  const finalTime = grid.at(-1);
  if (finalTime === undefined) {
    throw new Error('Simulation grid is empty.');
  }
  const finalEvaluation = evaluateClosedLoop(
    scenario,
    finalTime,
    state,
    lastNoise,
    'right',
  );
  samples.push(sampleFromEvaluation(finalTime, state, finalEvaluation));
  return { state: 'completed', samples, eventAlignedGrid: grid };
}

function positivityConditions(coefficients: readonly number[]): StabilityCondition[] {
  return coefficients.map((coefficient, index) => ({
    id: `coefficient_${index}_positive`,
    description: `Coefficient ${index} must be strictly positive.`,
    left: coefficient,
    comparator: '>',
    right: 0,
    passed: coefficient > 0,
  }));
}

export function evaluateLinearStability(
  plant: PlantParameters,
  controller: ControllerParameters,
): StabilityResult {
  validateScenario({
    mode: 'aircraft_pitch_surrogate',
    plant,
    controller,
    actuator: { maximumTorque: 1 },
    reference: { kind: 'constant', value: 0 },
    disturbance: { kind: 'none' },
    noise: {
      enabled: false,
      seed: 0,
      angleStandardDeviation: 0,
      rateStandardDeviation: 0,
    },
    initialState: {
      angle: 0,
      angularRate: 0,
      integralTorque: 0,
      filteredMeasuredRate: 0,
    },
    simulation: { startTime: 0, endTime: 1, nominalStep: 0.1 },
  });
  const { inertia: I, damping: c, stiffness: k } = plant;
  const {
    kp: Kp,
    ki: Ki,
    kd: Kd,
    derivativeFilterTimeConstant: tau,
  } = controller;
  let branch: StabilityBranch;
  let coefficients: number[];
  let determinantConditions: StabilityCondition[] = [];
  if (Ki === 0 && tau === 0) {
    branch = 'second_order_no_integral_no_filter';
    coefficients = [I, c + Kd, k + Kp];
  } else if (Ki > 0 && tau === 0) {
    branch = 'cubic_integral_no_filter';
    const a3 = I;
    const a2 = c + Kd;
    const a1 = k + Kp;
    const a0 = Ki;
    coefficients = [a3, a2, a1, a0];
    determinantConditions = [
      {
        id: 'cubic_hurwitz_product',
        description: 'a2*a1 must be greater than a3*a0.',
        left: a2 * a1,
        comparator: '>',
        right: a3 * a0,
        passed: a2 * a1 > a3 * a0,
      },
    ];
  } else if (Ki === 0 && tau > 0) {
    branch = 'cubic_no_integral_with_filter';
    const a3 = tau * I;
    const a2 = I + tau * c;
    const a1 = c + tau * (k + Kp) + Kd;
    const a0 = k + Kp;
    coefficients = [a3, a2, a1, a0];
    determinantConditions = [
      {
        id: 'cubic_hurwitz_product',
        description: 'a2*a1 must be greater than a3*a0.',
        left: a2 * a1,
        comparator: '>',
        right: a3 * a0,
        passed: a2 * a1 > a3 * a0,
      },
    ];
  } else {
    branch = 'quartic_integral_with_filter';
    const a4 = tau * I;
    const a3 = I + tau * c;
    const a2 = c + tau * (k + Kp) + Kd;
    const a1 = k + Kp + tau * Ki;
    const a0 = Ki;
    coefficients = [a4, a3, a2, a1, a0];
    determinantConditions = [
      {
        id: 'quartic_hurwitz_first',
        description: 'a3*a2 must be greater than a4*a1.',
        left: a3 * a2,
        comparator: '>',
        right: a4 * a1,
        passed: a3 * a2 > a4 * a1,
      },
      {
        id: 'quartic_hurwitz_second',
        description: 'a3*a2*a1 must be greater than a4*a1^2 + a3^2*a0.',
        left: a3 * a2 * a1,
        comparator: '>',
        right: a4 * a1 ** 2 + a3 ** 2 * a0,
        passed: a3 * a2 * a1 > a4 * a1 ** 2 + a3 ** 2 * a0,
      },
    ];
  }
  const conditions = [...positivityConditions(coefficients), ...determinantConditions];
  return {
    state: conditions.every((condition) => condition.passed)
      ? 'stable_for_scoped_linear_model'
      : 'not_strictly_stable_for_scoped_linear_model',
    branch,
    coefficientsDescending: coefficients,
    conditions,
  };
}

export function trapezoidalIntegral(
  times: readonly number[],
  values: readonly number[],
): number {
  if (times.length !== values.length || times.length === 0) {
    throw new RangeError('times and values must have equal non-zero length.');
  }
  let sum = 0;
  for (let index = 0; index < times.length - 1; index += 1) {
    const t0 = times[index];
    const t1 = times[index + 1];
    const y0 = values[index];
    const y1 = values[index + 1];
    if (t0 === undefined || t1 === undefined || y0 === undefined || y1 === undefined) {
      throw new Error('Unexpected missing trapezoid value.');
    }
    if (!(t1 > t0)) {
      throw new RangeError('times must be strictly increasing.');
    }
    sum += 0.5 * (y0 + y1) * (t1 - t0);
  }
  return sum;
}

export function calculateIntegralMetrics(
  samples: readonly SimulationSample[],
): IntegralMetrics {
  const times = samples.map((sample) => sample.time);
  const startTime = times[0];
  if (startTime === undefined) {
    throw new RangeError('At least one sample is required.');
  }
  return {
    iae: trapezoidalIntegral(times, samples.map((sample) => Math.abs(sample.error))),
    ise: trapezoidalIntegral(times, samples.map((sample) => sample.error ** 2)),
    itae: trapezoidalIntegral(
      times,
      samples.map((sample) => (sample.time - startTime) * Math.abs(sample.error)),
    ),
    controlAbsolute: trapezoidalIntegral(
      times,
      samples.map((sample) => Math.abs(sample.appliedTorque)),
    ),
    controlSquared: trapezoidalIntegral(
      times,
      samples.map((sample) => sample.appliedTorque ** 2),
    ),
  };
}

export function calculateSaturationMetrics(
  samples: readonly SimulationSample[],
  maximumTorque: number,
): SaturationMetrics {
  requirePositive('maximumTorque', maximumTorque);
  if (samples.length === 0) {
    throw new RangeError('At least one sample is required.');
  }
  const times = samples.map((sample) => sample.time);
  const indicator = samples.map((sample) =>
    Math.abs(sample.rawTorque) > maximumTorque ? 1 : 0,
  );
  const duration = trapezoidalIntegral(times, indicator);
  const start = times[0];
  const end = times.at(-1);
  if (start === undefined || end === undefined || !(end > start)) {
    throw new RangeError('Saturation metrics require a positive-duration history.');
  }
  return {
    duration,
    fraction: duration / (end - start),
    maximumRawMagnitude: Math.max(...samples.map((sample) => Math.abs(sample.rawTorque))),
    maximumAppliedMagnitude: Math.max(
      ...samples.map((sample) => Math.abs(sample.appliedTorque)),
    ),
    firstExceedanceTime:
      samples.find((sample) => Math.abs(sample.rawTorque) > maximumTorque)?.time ?? null,
  };
}

function interpolateCrossingTime(
  time0: number,
  value0: number,
  time1: number,
  value1: number,
  threshold: number,
): number {
  if (value1 === value0) {
    return time0;
  }
  return time0 + ((threshold - value0) * (time1 - time0)) / (value1 - value0);
}

function firstUpwardCrossing(
  points: readonly { readonly time: number; readonly value: number }[],
  threshold: number,
  minimumTime: number,
): number | null {
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    if (point === undefined) {
      continue;
    }
    if (point.time >= minimumTime && point.value === threshold) {
      return point.time;
    }
    const next = points[index + 1];
    if (
      next !== undefined &&
      point.value < threshold &&
      next.value >= threshold
    ) {
      const crossing = interpolateCrossingTime(
        point.time,
        point.value,
        next.time,
        next.value,
        threshold,
      );
      if (crossing >= minimumTime) {
        return crossing;
      }
    }
  }
  return null;
}

export function calculateStepMetrics(
  samples: readonly SimulationSample[],
  profile: Extract<ReferenceProfile, { readonly kind: 'step' }>,
  settings: StepMetricSettings,
): StepMetrics {
  if (samples.length < 2) {
    throw new RangeError('At least two samples are required.');
  }
  requireNonNegative('riseLowerFraction', settings.riseLowerFraction);
  requireNonNegative('riseUpperFraction', settings.riseUpperFraction);
  if (
    settings.riseLowerFraction >= settings.riseUpperFraction ||
    settings.riseUpperFraction > 1
  ) {
    throw new RangeError('Rise thresholds must satisfy 0 <= low < high <= 1.');
  }
  requireNonNegative('settlingHoldDuration', settings.settlingHoldDuration);
  requirePositive('settling band', settings.settlingBand.value);
  const stepIndex = samples.findIndex((sample) =>
    sameConstructedTime(sample.time, profile.time),
  );
  if (stepIndex < 0) {
    throw new RangeError('The step time must exist in the event-aligned output.');
  }
  const stepSample = samples[stepIndex];
  const finalSample = samples.at(-1);
  if (stepSample === undefined || finalSample === undefined) {
    throw new Error('Unexpected missing sample.');
  }
  const initialAngle = stepSample.angle;
  const amplitude = profile.after - initialAngle;
  const finalError = profile.after - finalSample.angle;
  if (amplitude === 0) {
    const settlingTime =
      settings.settlingBand.kind === 'absolute'
        ? calculateSettlingTime(samples, stepIndex, profile.after, amplitude, settings)
        : null;
    return {
      riseTime: null,
      riseTimeReason: 'Zero step amplitude relative to the state at the event.',
      overshootPercent: null,
      overshootReason: 'Zero step amplitude relative to the state at the event.',
      settlingTime,
      settlingReason:
        settings.settlingBand.kind === 'fraction_of_step'
          ? 'A fractional settling band is undefined for zero step amplitude.'
          : settlingTime === null
            ? 'Response did not settle under the configured finite-window rule.'
            : null,
      finalError,
    };
  }
  const normalised = samples.slice(stepIndex).map((sample) => ({
    time: sample.time,
    value: (sample.angle - initialAngle) / amplitude,
  }));
  const lowTime = firstUpwardCrossing(
    normalised,
    settings.riseLowerFraction,
    profile.time,
  );
  const highTime =
    lowTime === null
      ? null
      : firstUpwardCrossing(normalised, settings.riseUpperFraction, lowTime);
  const maximumNormalised = Math.max(...normalised.map((point) => point.value));
  const settlingTime = calculateSettlingTime(
    samples,
    stepIndex,
    profile.after,
    amplitude,
    settings,
  );
  return {
    riseTime: lowTime === null || highTime === null ? null : highTime - lowTime,
    riseTimeReason:
      lowTime === null || highTime === null
        ? 'Required rise-threshold crossings were not achieved in order.'
        : null,
    overshootPercent: Math.max(0, maximumNormalised - 1) * 100,
    overshootReason: null,
    settlingTime,
    settlingReason:
      settlingTime === null ? 'Response did not settle under the configured finite-window rule.' : null,
    finalError,
  };
}

function calculateSettlingTime(
  samples: readonly SimulationSample[],
  stepIndex: number,
  target: number,
  stepAmplitude: number,
  settings: StepMetricSettings,
): number | null {
  const band =
    settings.settlingBand.kind === 'absolute'
      ? settings.settlingBand.value
      : settings.settlingBand.value * Math.abs(stepAmplitude);
  const finalTime = samples.at(-1)?.time;
  if (finalTime === undefined) {
    return null;
  }
  let lastOutsideIndex = -1;
  for (let index = stepIndex; index < samples.length; index += 1) {
    const sample = samples[index];
    if (sample !== undefined && Math.abs(sample.angle - target) > band) {
      lastOutsideIndex = index;
    }
  }
  let candidateTime: number;
  if (lastOutsideIndex < stepIndex) {
    const first = samples[stepIndex];
    if (first === undefined) {
      return null;
    }
    candidateTime = first.time;
  } else {
    const outside = samples[lastOutsideIndex];
    const inside = samples[lastOutsideIndex + 1];
    if (outside === undefined || inside === undefined) {
      return null;
    }
    const outsideError = outside.angle - target;
    const boundary = target + Math.sign(outsideError) * band;
    candidateTime = interpolateCrossingTime(
      outside.time,
      outside.angle,
      inside.time,
      inside.angle,
      boundary,
    );
  }
  return finalTime - candidateTime >= settings.settlingHoldDuration
    ? candidateTime
    : null;
}

function interpolateSample(
  samples: readonly SimulationSample[],
  time: number,
): Pick<SimulationSample, 'angle' | 'angularRate' | 'appliedTorque'> {
  const exact = samples.find((sample) => sameConstructedTime(sample.time, time));
  if (exact !== undefined) {
    return exact;
  }
  for (let index = 0; index < samples.length - 1; index += 1) {
    const left = samples[index];
    const right = samples[index + 1];
    if (left === undefined || right === undefined) {
      continue;
    }
    if (left.time < time && time < right.time) {
      const fraction = (time - left.time) / (right.time - left.time);
      return {
        angle: left.angle + fraction * (right.angle - left.angle),
        angularRate:
          left.angularRate + fraction * (right.angularRate - left.angularRate),
        appliedTorque:
          left.appliedTorque + fraction * (right.appliedTorque - left.appliedTorque),
      };
    }
  }
  throw new RangeError(`Time ${time} is outside the fine history.`);
}

function maximumDifference(
  coarse: readonly SimulationSample[],
  fine: readonly SimulationSample[],
  key: 'angle' | 'angularRate' | 'appliedTorque',
  criterion: RefinementCriterion,
): MaximumDifference {
  requireNonNegative(`${key} tolerance`, criterion.tolerance);
  let maximumAbsolute = -1;
  let signedAtMaximum = 0;
  let timeAtMaximum = coarse[0]?.time ?? 0;
  for (const sample of coarse) {
    const fineValue = interpolateSample(fine, sample.time)[key];
    const signed = sample[key] - fineValue;
    const absolute = Math.abs(signed);
    if (absolute > maximumAbsolute) {
      maximumAbsolute = absolute;
      signedAtMaximum = signed;
      timeAtMaximum = sample.time;
    }
  }
  if (!criterion.selected) {
    return {
      absolute: maximumAbsolute,
      signed: signedAtMaximum,
      time: timeAtMaximum,
      state: 'not_assessed',
      tolerance: null,
    };
  }
  return {
    absolute: maximumAbsolute,
    signed: signedAtMaximum,
    time: timeAtMaximum,
    state: maximumAbsolute <= criterion.tolerance ? 'met' : 'not_met',
    tolerance: criterion.tolerance,
  };
}

export function compareTimeSteps(
  coarse: SimulationResult,
  fine: SimulationResult,
  criteria: RefinementCriteria,
  noiseEnabled: boolean,
): RefinementResult {
  const angle = maximumDifference(coarse.samples, fine.samples, 'angle', criteria.angle);
  const angularRate = maximumDifference(
    coarse.samples,
    fine.samples,
    'angularRate',
    criteria.angularRate,
  );
  const appliedTorque = maximumDifference(
    coarse.samples,
    fine.samples,
    'appliedTorque',
    criteria.appliedTorque,
  );
  if (noiseEnabled) {
    return {
      state: 'not_assessed',
      reason: 'Formal deterministic time-step refinement is not assessed while noise is enabled.',
      angle,
      angularRate,
      appliedTorque,
    };
  }
  const selected = [angle, angularRate, appliedTorque].filter(
    (result) => result.state !== 'not_assessed',
  );
  if (selected.length === 0) {
    return {
      state: 'not_assessed',
      reason: 'No numerical discrepancy criterion was selected.',
      angle,
      angularRate,
      appliedTorque,
    };
  }
  const state = selected.every((result) => result.state === 'met')
    ? 'met'
    : 'not_met';
  return {
    state,
    reason:
      state === 'met'
        ? 'Every selected discrepancy meets its recorded tolerance.'
        : 'At least one selected discrepancy exceeds its recorded tolerance.',
    angle,
    angularRate,
    appliedTorque,
  };
}

function nullableAscending(value: number | null): number {
  return value === null ? Number.POSITIVE_INFINITY : value;
}

/**
 * Build the canonical candidate identity from its zero-based Cartesian
 * enumeration ordinal. Lexical order is guaranteed to equal ordinal order.
 */
export function makeCandidateId(ordinal: number, totalCandidateCount: number): string {
  if (!Number.isSafeInteger(totalCandidateCount) || totalCandidateCount <= 0) {
    throw new RangeError('totalCandidateCount must be a positive safe integer.');
  }
  if (
    !Number.isSafeInteger(ordinal) ||
    ordinal < 0 ||
    ordinal >= totalCandidateCount
  ) {
    throw new RangeError('ordinal must be a safe integer inside the candidate range.');
  }
  const width = Math.max(1, String(totalCandidateCount - 1).length);
  return `C${String(ordinal).padStart(width, '0')}`;
}

export function rankFeasibleCandidates(
  candidates: readonly TuningCandidate[],
): TuningCandidate | null {
  const feasible = candidates.filter(
    (candidate) => candidate.feasible && candidate.objective !== null,
  );
  feasible.sort((a, b) => {
    const keysA = [
      nullableAscending(a.objective),
      nullableAscending(a.overshootPercent),
      nullableAscending(a.saturationFraction),
      nullableAscending(a.controlSquared),
      a.kp,
      a.ki,
      a.kd,
      a.derivativeFilterTimeConstant,
      a.trackingTimeConstant,
    ];
    const keysB = [
      nullableAscending(b.objective),
      nullableAscending(b.overshootPercent),
      nullableAscending(b.saturationFraction),
      nullableAscending(b.controlSquared),
      b.kp,
      b.ki,
      b.kd,
      b.derivativeFilterTimeConstant,
      b.trackingTimeConstant,
    ];
    for (let index = 0; index < keysA.length; index += 1) {
      const left = keysA[index];
      const right = keysB[index];
      if (left === undefined || right === undefined) {
        continue;
      }
      if (left < right) return -1;
      if (left > right) return 1;
    }
    return a.id.localeCompare(b.id);
  });
  return feasible[0] ?? null;
}
